import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  HeartIcon,
  HeartFilledIcon,
  CommentIcon,
  FlagIcon,
  FlagFilledIcon,
  TagIcon,
  TrashIcon,
} from "../assets/icons/index.js";
import BorderBox from "./BorderBox.jsx";
import requestHandler from "../utils/requestHandler.js";
import topics from "../data/topics.js";
import { useGlobalContext } from "../context/GlobalContext.jsx";

export default function Post({
  post,
  disableCommentInput = false,
  enableNavigation = true,
  isIndividualPostPage = false,
  onCommentSubmit,
}) {
  const {
    id = "1",
    title = "",
    content = "",
    user = null,
    topic = null,
    likes_count = null,
    user_liked = false,
    user_flagged = false,
    comments = [],
  } = post || {};

  const [isLiked, setIsLiked] = useState(user_liked);
  const [isFlagged, setIsFlagged] = useState(user_flagged);
  const [likesCount, setLikesCount] = useState(likes_count);
  const [comment, setComment] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const navigate = useNavigate();
  const commentInputRef = useRef(null);
  const { user: currentUser } = useGlobalContext();

  const handleLike = async () => {
    setIsLiked(!isLiked);
    setLikesCount(isLiked ? likesCount - 1 : likesCount + 1);

    const res = await requestHandler({
      path: "likes/toggle",
      method: "POST",
      body: { post_id: id },
    });

    if (!res.success) {
      setIsLiked(!isLiked);
      setLikesCount(isLiked ? likesCount - 1 : likesCount + 1);
      console.error("Error toggling like:", res.error);
    }
  };

  const handleFlag = async () => {
    setIsFlagged(!isFlagged);

    //TODO: body: { comment_id: id }
    const res = await requestHandler({
      path: "flags/toggle",
      method: "POST",
      body: { post_id: id },
    });

    if (!res.success) {
      setIsFlagged(!isFlagged);
      console.error("Error toggling flag:", res.error);
    }
  };

  const handleComment = async () => {
    if (disableCommentInput) {
      navigate(`/post/${id}`);
      return;
    }

    if (comment.trim() && onCommentSubmit) {
      setIsSubmittingComment(true);
      const result = await onCommentSubmit(comment);
      if (!result?.flagged) {
        setComment("");
      }
      setIsSubmittingComment(false);
    }
  };

  const handleCommentIconClick = () => {
    if (isIndividualPostPage) {
      // Focus the comment input when on individual post page
      commentInputRef.current?.focus();
    } else {
      // Navigate to post page when on community page
      navigate(`/post/${id}`);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);
    const res = await requestHandler({
      path: `posts/${id}`,
      method: "DELETE",
    });

    if (!res.success) {
      console.error("Error deleting post:", res.error);
    } else {
      if (isIndividualPostPage) {
        navigate("/community");
      } else {
        window.location.reload();
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  // Auto-resize textarea as user types
  useEffect(() => {
    if (commentInputRef.current) {
      commentInputRef.current.style.height = "auto";
      commentInputRef.current.style.height =
        commentInputRef.current.scrollHeight + "px";
    }
  }, [comment]);

  const DeleteConfirmationModal = ({ isOpen, onConfirm, onCancel }) => {
    if (!isOpen) return null;

    return (
      <>
        {/* Overlay for the entire app */}
        <div className="fixed inset-0 bg-black/50 z-50" onClick={onCancel} />

        {/* Modal content */}
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          onClick={onCancel}
        >
          <div
            className="bg-white dark:bg-lz-ink-mid text-lz-ink-mid dark:text-white rounded-2xl p-6 shadow-lg w-96"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-semibold mb-4">Delete Post</h3>
            <p className="mb-6">
              Are you sure you want to delete this post? This action cannot be
              undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={onCancel}
                className="px-4 py-2 text-lz-ink-mid dark:text-white hover:bg-gray-100 dark:hover:bg-lz-charcoal rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
      <BorderBox padding="p-6" className="h-fit relative">
        {/* Trash Icon - only show if current user owns the post */}
        {currentUser?.id && user?.id && currentUser.id === user.id && (
          <button
            onClick={handleDeleteClick}
            className="absolute top-5 right-5 p-2 hover:bg-white dark:hover:bg-lz-charcoal transition-colors"
          >
            <TrashIcon className="w-5 h-5 fill-lz-charcoal dark:fill-lz-concrete hover:fill-red-500 dark:hover:fill-red-400 transition-colors" />
            <span className="sr-only">Delete post</span>
          </button>
        )}

        {/* User Profile Section */}
        <div className="flex flex-row items-center gap-3 mb-4">
          <span className="font-semibold text-lz-ember dark:text-lz-gold-border">
            {user?.name || ""}
          </span>
          <span className="font-semibold text-xs italic mt-0.5 text-lz-ember dark:text-lz-gold-border">
            {post?.created_at
              ? new Date(post.created_at).toLocaleDateString("en-US")
              : ""}
          </span>
        </div>

        {/* Post Content */}
        <div className="mb-6">
          <h2
            className={`text-xl font-bold text-black dark:text-lz-concrete mb-3 ${
              enableNavigation ? "cursor-pointer" : ""
            }`}
            onClick={
              enableNavigation ? () => navigate(`/post/${id}`) : undefined
            }
          >
            {title}
          </h2>
          <p className="text-black dark:text-lz-concrete leading-relaxed">
            {content.split("\n").map((item) => {
              return (
                <>
                  {item}
                  <br />
                </>
              );
            })}
          </p>
        </div>

        {/* Topic Section */}
        <button
          onClick={() => navigate("/community", { state: { selectedTopic: topic } })}
          className="flex items-center gap-2 mb-4 cursor-pointer hover:opacity-80 transition-opacity"
        >
          <TagIcon className="w-5 h-5 fill-lz-ember dark:fill-lz-candle-bright" />
          <span className="font-semibold text-lz-ember dark:text-lz-candle-bright">
            {topics.find((t) => t.index === topic)?.label || ""}
          </span>
        </button>

        {/* Interaction Section */}
        <div className="flex items-center gap-4 mb-4">
          {/* Like Button */}
          <button onClick={handleLike} className={`p-2 rounded-full`}>
            {isLiked ? (
              <HeartFilledIcon className="w-5 h-5 fill-lz-ember" />
            ) : (
              <HeartIcon className="w-5 h-5 dark:fill-white" />
            )}
            <span className="sr-only">{isLiked ? "Unlike" : "Like"}</span>
          </button>
          <span className="text-sm text-lz-ink-mid dark:text-lz-concrete -ml-3">
            {likesCount}
          </span>

          {/* Comment Button */}
          <button className="p-2 rounded-full" onClick={handleCommentIconClick}>
            <CommentIcon className="w-5 h-5 dark:fill-white fill-black" />
            <span className="sr-only">Comment</span>
          </button>
          <span className="text-sm text-lz-ink-mid dark:text-lz-concrete -ml-3">
            {comments.length}
          </span>

          {/* Flag Button */}
          <button onClick={handleFlag} className="p-2 rounded-full ml-auto">
            {isFlagged ? (
              <FlagFilledIcon className="w-5 h-5 fill-lz-ember" />
            ) : (
              <FlagIcon className="w-5 h-5 dark:fill-white fill-black" />
            )}
            <span className="sr-only">{isFlagged ? "Unflag" : "Flag"}</span>
          </button>
        </div>

        {/* Comment Input */}
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-3">
            <label htmlFor={`comment-${id}`} className="sr-only">Add a comment</label>
            <textarea
              id={`comment-${id}`}
              ref={commentInputRef}
              placeholder="Add a comment"
              value={disableCommentInput ? "" : comment}
              onChange={
                disableCommentInput
                  ? undefined
                  : (e) => setComment(e.target.value)
              }
              onKeyDown={
                disableCommentInput
                  ? undefined
                  : (e) => {
                      if (
                        e.key === "Enter" &&
                        e.ctrlKey &&
                        !isSubmittingComment
                      ) {
                        handleComment();
                      }
                    }
              }
              onClick={
                disableCommentInput ? () => navigate(`/post/${id}`) : undefined
              }
              readOnly={disableCommentInput || isSubmittingComment}
              disabled={isSubmittingComment}
              className={`flex-1 px-4 py-2 rounded-2xl border border-lz-paper-border dark:border-lz-charcoal-border bg-lz-concrete dark:bg-lz-ink text-black dark:text-lz-concrete placeholder-lz-ink-mid dark:placeholder-lz-concrete focus:outline-none focus:ring-2 focus:ring-lz-candle-dim resize-none overflow-hidden ${
                disableCommentInput ? "cursor-pointer" : ""
              } ${isSubmittingComment ? "opacity-50" : ""}`}
              rows="1"
            />
            {comment.trim() && !disableCommentInput && (
              <button
                onClick={handleComment}
                disabled={isSubmittingComment}
                className="hidden sm:block px-4 py-2 bg-lz-ember border-2 dark:bg-lz-ink-mid dark:border-lz-candle-dim border-lz-ember hover:border-lz-ink-mid text-white rounded-full hover:bg-lz-ink-mid dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink-mid transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmittingComment ? "Posting..." : "Post"}
              </button>
            )}
          </div>
          {comment.trim() && !disableCommentInput && (
            <div className="flex sm:hidden justify-end">
              <button
                onClick={handleComment}
                disabled={isSubmittingComment}
                className="px-6 py-2 bg-lz-ember border-2 dark:bg-lz-ink-mid dark:border-lz-candle-dim border-lz-ember hover:border-lz-ink-mid text-white rounded-full hover:bg-lz-ink-mid dark:hover:bg-lz-candle-dim dark:hover:text-lz-ink-mid transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmittingComment ? "Posting..." : "Post"}
              </button>
            </div>
          )}
        </div>
      </BorderBox>
    </>
  );
}
